/**
 * Persona-Scope (#140): welcher Projektbestand zu einer Persona gehört.
 *
 * Eine Definition für Phase Board (abschaltbarer Default-Filter, `computePersonaFilter`)
 * und Mission Control (harter Scope der Kennzahlen + `PersonaSection`). Vorher gab es drei
 * Varianten: Board-Manager nur über Tags, Mission-Control-Kennzahlen für PL/Manager global,
 * `PersonaSection` nur über das Manager-Feld.
 *
 * - kl: global (auch Admins, die als KL schauen)
 * - bl: eigene Segmente (ohne Segment global)
 * - pl: `project_lead_user_id = ich`
 * - manager: eigener Tag (`tags[].manager_user_id = ich`) ODER `manager_user_id = ich`,
 *   eingegrenzt auf die eigenen Manager-Segmente (ohne Segment keine Eingrenzung)
 * - mtb / service: Bereich MTB bzw. Service, eingegrenzt auf die eigenen Segmente
 *
 * `null` heißt global. Das Label des Filter-Chips bleibt im Board.
 */
import type { Persona } from '@projektverwaltung/shared';

export type PersonaScopeProjekt = {
  segment: string;
  responsibility?: string | null;
  project_lead_user_id?: string | null;
  manager_user_id?: string | null;
  tags?: ReadonlyArray<{ manager_user_id?: string | null }>;
};

export type PersonaSegmente = {
  bl: string[];
  manager: string[];
  mtb: string[];
  service: string[];
};

/** Segmente je segmentpflichtiger Rolle aus den Funktionsrollen des Nutzers. */
export function personaSegmente(
  functionRoles: ReadonlyArray<{ role: string; scope_segment: string | null }>,
): PersonaSegmente {
  const fuer = (role: string) =>
    functionRoles
      .filter((fr) => fr.role === role)
      .map((fr) => fr.scope_segment)
      .filter((s): s is string => s !== null);
  return {
    bl: fuer('bereichsleiter'),
    manager: fuer('manager'),
    mtb: fuer('mtb'),
    service: fuer('service_leitung'),
  };
}

export function personaPraedikat(
  persona: Persona,
  myUserId: string | undefined,
  segmente: PersonaSegmente,
): ((p: PersonaScopeProjekt) => boolean) | null {
  const imSegment = (liste: string[], p: PersonaScopeProjekt) =>
    liste.length === 0 || liste.includes(p.segment);

  switch (persona) {
    case 'pl':
      return myUserId ? (p) => p.project_lead_user_id === myUserId : null;
    case 'bl':
      return segmente.bl.length > 0 ? (p) => segmente.bl.includes(p.segment) : null;
    case 'manager':
      return myUserId
        ? (p) =>
            imSegment(segmente.manager, p) &&
            (p.manager_user_id === myUserId ||
              (p.tags ?? []).some((tg) => tg.manager_user_id === myUserId))
        : null;
    case 'mtb':
      return (p) => p.responsibility === 'MTB' && imSegment(segmente.mtb, p);
    case 'service':
      return (p) => p.responsibility === 'Service' && imSegment(segmente.service, p);
    default:
      return null;
  }
}
